import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Skeleton,
  Tabs,
  Tab,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  benachrichtigungenService,
  GlobalGotifyUrlResponse,
  GotifyDeliveryMode,
} from '../services/benachrichtigungenService';
import {
  emailAdminService,
  EmailDeliveryMode,
  EmailTechnicalConfig,
} from '../services/emailAdminService';
import { statusService, auditStatusService } from '../services/lookupService';
import { urlaubService } from '../services/urlaubService';
import { aprilscherzService } from '../services/aprilscherzService';
import { saisonService } from '../services/saisonService';
import rechnungenService from '../services/rechnungenService';
import { Status, AuditStatus, RechnungAbsender, UrlaubSettings } from '../types';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useNotification } from '../hooks/NotificationContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

interface ColumnConfig<T> {
  label: string;
  render: (item: T) => React.ReactNode;
}

interface LookupTableProps<T extends { id: number }> {
  items: T[];
  onAdd: () => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  loading: boolean;
  columns: ColumnConfig<T>[];
  onMoveUp?: (index: number) => void;
  onMoveDown?: (index: number) => void;
}

function LookupTable<T extends { id: number }>({
  items,
  onAdd,
  onEdit,
  onDelete,
  loading,
  columns,
  onMoveUp,
  onMoveDown,
}: LookupTableProps<T>) {
  const sortable = !!(onMoveUp && onMoveDown);

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" onClick={onAdd}>
            Hinzufügen
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {sortable && <TableCell sx={{ width: 100 }}>Reihenfolge</TableCell>}
                {columns.map((col, idx) => (
                  <TableCell key={idx}>{col.label}</TableCell>
                ))}
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (sortable ? 2 : 1)} align="center">
                    Keine Einträge gefunden
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, index) => (
                  <TableRow key={item.id} hover>
                    {sortable && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton
                          size="small"
                          onClick={() => onMoveUp!(index)}
                          disabled={index === 0}
                          title="Nach oben"
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onMoveDown!(index)}
                          disabled={index === items.length - 1}
                          title="Nach unten"
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                    {columns.map((col, idx) => (
                      <TableCell key={idx}>{col.render(item)}</TableCell>
                    ))}
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => onEdit(item)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => onDelete(item)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

interface EditDialogProps {
  open: boolean;
  title: string;
  name: string;
  nameLabel: string;
  onNameChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
  loading?: boolean;
}

const EditDialog: React.FC<EditDialogProps> = ({
  open,
  title,
  name,
  nameLabel,
  onNameChange,
  onClose,
  onSave,
  loading = false,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={nameLabel}
          fullWidth
          variant="outlined"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button onClick={onSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SystemkonfigurationPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess } = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState<Status[]>([]);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);
  const [statusName, setStatusName] = useState('');

  const [auditStatuses, setAuditStatuses] = useState<AuditStatus[]>([]);
  const [auditStatusLoading, setAuditStatusLoading] = useState(true);
  const [auditStatusDialogOpen, setAuditStatusDialogOpen] = useState(false);
  const [editingAuditStatus, setEditingAuditStatus] = useState<AuditStatus | null>(null);
  const [auditStatusName, setAuditStatusName] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    type: 'status' | 'auditStatus';
    item: { id: number };
    displayName: string;
  } | null>(null);

  const [globalGotifyUrl, setGlobalGotifyUrl] = useState('');
  const [globalGotifyEnvHint, setGlobalGotifyEnvHint] = useState<string | null>(null);
  const [globalGotifyAppEnv, setGlobalGotifyAppEnv] = useState<string>('local');
  const [globalGotifyDeliveryMode, setGlobalGotifyDeliveryMode] = useState<GotifyDeliveryMode>('suppress');
  const [globalGotifyModeEnv, setGlobalGotifyModeEnv] = useState<string>('suppress');
  const [globalGotifyModeInDb, setGlobalGotifyModeInDb] = useState(false);
  const [gotifyLoading, setGotifyLoading] = useState(false);
  const [gotifySaving, setGotifySaving] = useState(false);

  const [urlaubSettings, setUrlaubSettings] = useState<UrlaubSettings | null>(null);
  const [urlaubLoading, setUrlaubLoading] = useState(false);
  const [urlaubSaving, setUrlaubSaving] = useState(false);
  const [urlaubAutoUebertrag, setUrlaubAutoUebertrag] = useState(true);
  const [urlaubHinweisMonat, setUrlaubHinweisMonat] = useState(3);
  const [urlaubHinweisTag, setUrlaubHinweisTag] = useState(31);

  const [absender, setAbsender] = useState<RechnungAbsender | null>(null);
  const [absenderForm, setAbsenderForm] = useState<Partial<RechnungAbsender>>({});
  const [absenderLoading, setAbsenderLoading] = useState(false);
  const [absenderSaving, setAbsenderSaving] = useState(false);

  const [aprilAktiv, setAprilAktiv] = useState(true);
  const [aprilLoading, setAprilLoading] = useState(false);
  const [aprilSaving, setAprilSaving] = useState(false);
  const [saisonAktiv, setSaisonAktiv] = useState(true);
  const [saisonLoading, setSaisonLoading] = useState(false);
  const [saisonSaving, setSaisonSaving] = useState(false);

  const applyGotifySettingsResponse = (d: GlobalGotifyUrlResponse) => {
    setGlobalGotifyUrl(d.gotify_base_url_stored || '');
    setGlobalGotifyEnvHint(d.gotify_base_url_env || null);
    setGlobalGotifyAppEnv(d.app_env || 'local');
    const m = d.gotify_delivery_mode === 'send' || d.gotify_delivery_mode === 'allowlist' ? d.gotify_delivery_mode : 'suppress';
    setGlobalGotifyDeliveryMode(m);
    setGlobalGotifyModeEnv(d.gotify_delivery_mode_environment || 'suppress');
    setGlobalGotifyModeInDb(!!d.gotify_delivery_mode_in_database);
  };

  const [emailCfg, setEmailCfg] = useState<EmailTechnicalConfig | null>(null);
  const [emailSesRegion, setEmailSesRegion] = useState('');
  const [emailSesFrom, setEmailSesFrom] = useState('');
  const [emailDeliveryMode, setEmailDeliveryMode] = useState<EmailDeliveryMode>('suppress');
  const [emailAllowlist, setEmailAllowlist] = useState('');
  const [emailAccessKey, setEmailAccessKey] = useState('');
  const [emailSecretKey, setEmailSecretKey] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);

  const loadStatus = async () => {
    try {
      setStatusLoading(true);
      const data = await statusService.list();
      setStatus(data);
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setStatusLoading(false);
    }
  };

  const loadAuditStatuses = async () => {
    try {
      setAuditStatusLoading(true);
      const data = await auditStatusService.list();
      setAuditStatuses(data);
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setAuditStatusLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    loadAuditStatuses();
  }, []);

  const loadGlobalGotify = async () => {
    try {
      setGotifyLoading(true);
      const d = await benachrichtigungenService.getGlobalGotifyUrl();
      applyGotifySettingsResponse(d);
    } catch {
      // Fehler: Axios-Interceptor
    } finally {
      setGotifyLoading(false);
    }
  };

  const loadEmailTechnical = async () => {
    try {
      setEmailLoading(true);
      const d = await emailAdminService.getTechnicalConfig();
      setEmailCfg(d);
      setEmailSesRegion(d.ses_region || '');
      setEmailSesFrom(d.ses_from_email || '');
      setEmailDeliveryMode(d.email_delivery_mode);
      setEmailAllowlist(d.notification_recipient_allowlist || '');
      setEmailAccessKey('');
      setEmailSecretKey('');
    } catch {
      // Interceptor
    } finally {
      setEmailLoading(false);
    }
  };

  const loadUrlaubSettings = async () => {
    try {
      setUrlaubLoading(true);
      const d = await urlaubService.getEinstellungen();
      setUrlaubSettings(d);
      setUrlaubAutoUebertrag(d.auto_uebertrag_aktiv);
      setUrlaubHinweisMonat(d.uebertrag_hinweis_monat);
      setUrlaubHinweisTag(d.uebertrag_hinweis_tag);
    } catch {
      // Interceptor
    } finally {
      setUrlaubLoading(false);
    }
  };

  const loadAbsender = async () => {
    try {
      setAbsenderLoading(true);
      const d = await rechnungenService.getEinstellungen();
      setAbsender(d);
      setAbsenderForm(d);
    } catch {
      // Interceptor
    } finally {
      setAbsenderLoading(false);
    }
  };

  const loadAprilscherz = async () => {
    try {
      setAprilLoading(true);
      const status = await aprilscherzService.getStatus();
      setAprilAktiv(status.aktiv);
    } catch {
      // Interceptor
    } finally {
      setAprilLoading(false);
    }
  };

  const loadSaison = async () => {
    try {
      setSaisonLoading(true);
      const status = await saisonService.getStatus();
      setSaisonAktiv(status.aktiv);
    } catch {
      // Interceptor
    } finally {
      setSaisonLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 2) {
      loadGlobalGotify();
    }
    if (tabValue === 3) {
      loadEmailTechnical();
    }
    if (tabValue === 4) {
      loadUrlaubSettings();
    }
    if (tabValue === 5) {
      loadAbsender();
    }
    if (tabValue === 6) {
      loadAprilscherz();
      loadSaison();
    }
  }, [tabValue]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleStatusAdd = () => {
    setEditingStatus(null);
    setStatusName('');
    setStatusDialogOpen(true);
  };

  const handleStatusEdit = (statusItem: Status) => {
    setEditingStatus(statusItem);
    setStatusName(statusItem.name);
    setStatusDialogOpen(true);
  };

  const handleStatusSave = async () => {
    try {
      setLoading(true);
      if (editingStatus) {
        await statusService.update(editingStatus.id, { name: statusName });
        showSuccess('Status erfolgreich aktualisiert');
      } else {
        await statusService.create({ name: statusName, sort_order: 0 });
        showSuccess('Status erfolgreich erstellt');
      }
      setStatusDialogOpen(false);
      await loadStatus();
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleStatusDelete = (statusItem: Status) => {
    setItemToDelete({ type: 'status', item: statusItem, displayName: statusItem.name });
    setDeleteDialogOpen(true);
  };

  const handleStatusMoveUp = (index: number) => {
    handleStatusMove(index, index - 1);
  };

  const handleStatusMoveDown = (index: number) => {
    handleStatusMove(index, index + 1);
  };

  const handleStatusMove = async (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= status.length) return;
    const reordered = [...status];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setStatus(reordered);
    try {
      const orderedIds = reordered.map((s) => s.id);
      const updated = await statusService.reorder(orderedIds);
      setStatus(updated);
    } catch {
      await loadStatus();
    }
  };

  const handleAuditStatusAdd = () => {
    setEditingAuditStatus(null);
    setAuditStatusName('');
    setAuditStatusDialogOpen(true);
  };

  const handleAuditStatusEdit = (item: AuditStatus) => {
    setEditingAuditStatus(item);
    setAuditStatusName(item.name);
    setAuditStatusDialogOpen(true);
  };

  const handleAuditStatusSave = async () => {
    try {
      setLoading(true);
      if (editingAuditStatus) {
        await auditStatusService.update(editingAuditStatus.id, { name: auditStatusName });
        showSuccess('Audit-Status erfolgreich aktualisiert');
      } else {
        await auditStatusService.create({ name: auditStatusName, sort_order: 0 });
        showSuccess('Audit-Status erfolgreich erstellt');
      }
      setAuditStatusDialogOpen(false);
      await loadAuditStatuses();
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleAuditStatusDelete = (item: AuditStatus) => {
    setItemToDelete({ type: 'auditStatus', item, displayName: item.name });
    setDeleteDialogOpen(true);
  };

  const handleAuditStatusMoveUp = (index: number) => {
    handleAuditStatusMove(index, index - 1);
  };

  const handleAuditStatusMoveDown = (index: number) => {
    handleAuditStatusMove(index, index + 1);
  };

  const handleAuditStatusMove = async (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= auditStatuses.length) return;
    const reordered = [...auditStatuses];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setAuditStatuses(reordered);
    try {
      const orderedIds = reordered.map((s) => s.id);
      const updated = await auditStatusService.reorder(orderedIds);
      setAuditStatuses(updated);
    } catch {
      await loadAuditStatuses();
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      setLoading(true);
      if (itemToDelete.type === 'status') {
        await statusService.delete(itemToDelete.item.id);
        showSuccess('Status erfolgreich gelöscht');
        await loadStatus();
      } else {
        await auditStatusService.delete(itemToDelete.item.id);
        showSuccess('Audit-Status erfolgreich gelöscht');
        await loadAuditStatuses();
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Systemkonfiguration"
        subtitle="Projekt- und Auditstatus, Gotify, E-Mail-Versand, Urlaub, E-Rechnung, Gimmicks"
        breadcrumbs={[{ label: 'Systemkonfiguration' }]}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Projektstatus" />
          <Tab label="Auditstatus" />
          <Tab label="Gotify (Push)" />
          <Tab label="E-Mail (SES)" />
          <Tab label="Urlaub" />
          <Tab label="E-Rechnung" />
          <Tab label="Gimmicks" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <LookupTable
          items={status}
          onAdd={handleStatusAdd}
          onEdit={handleStatusEdit}
          onDelete={handleStatusDelete}
          loading={statusLoading}
          columns={[{ label: 'Name', render: (item) => item.name }]}
          onMoveUp={handleStatusMoveUp}
          onMoveDown={handleStatusMoveDown}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <LookupTable
          items={auditStatuses}
          onAdd={handleAuditStatusAdd}
          onEdit={handleAuditStatusEdit}
          onDelete={handleAuditStatusDelete}
          loading={auditStatusLoading}
          columns={[{ label: 'Name', render: (item) => item.name }]}
          onMoveUp={handleAuditStatusMoveUp}
          onMoveDown={handleAuditStatusMoveDown}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Standard-Basis-URL des Gotify-Servers für alle Nutzerinnen und Nutzer, die keine eigene URL im
              Mitarbeiterprofil hinterlegt haben. Priorität: pro Person gespeicherte URL, dann dieser Wert, dann
              Umgebungsvariable <strong>GOTIFY_URL</strong> auf dem Anwendungsserver.
            </Typography>
            {globalGotifyDeliveryMode === 'suppress' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Kein echter Push-Versand:</strong> Im Backend ist{' '}
                <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                  GOTIFY_DELIVERY_MODE=suppress
                </Typography>{' '}
                (APP_ENV: <strong>{globalGotifyAppEnv}</strong>). Token und URL können trotzdem „Aktiv“ im Profil
                sein — der Versand wird nur protokolliert (
                <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                  gotify_suppressed_in_…
                </Typography>
                ). Lokal ohne Container-Neustart: unten <strong>Versand-Modus</strong> auf „send“ oder „allowlist“
                setzen und speichern (überschreibt die Umgebung in der Datenbank). Alternativ in der Shell vor{' '}
                <strong>docker compose</strong> die Variablen setzen (siehe <strong>docker-compose.yml</strong>).
              </Alert>
            )}
            {globalGotifyDeliveryMode === 'allowlist' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Modus <strong>allowlist</strong>: Pushes gehen nur an E-Mail-Adressen in{' '}
                <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                  NOTIFICATION_RECIPIENT_ALLOWLIST
                </Typography>{' '}
                (Kommaliste, Umgebung oder Systemkonfiguration E-Mail).
              </Alert>
            )}
            {globalGotifyEnvHint ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Aktuell gesetzte Umgebungsvariable GOTIFY_URL (Fallback): {globalGotifyEnvHint}
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Keine GOTIFY_URL in der Server-Umgebung. Ohne Eintrag hier und ohne URL pro Mitarbeiter ist Push
                nicht möglich.
              </Alert>
            )}
            {gotifyLoading ? (
              <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="gotify-delivery-mode-label">Gotify-Versand (effektiver Modus)</InputLabel>
                  <Select
                    labelId="gotify-delivery-mode-label"
                    label="Gotify-Versand (effektiver Modus)"
                    value={globalGotifyDeliveryMode}
                    onChange={(e) => setGlobalGotifyDeliveryMode(e.target.value as GotifyDeliveryMode)}
                  >
                    <MenuItem value="send">send — an alle Empfänger mit Token/URL</MenuItem>
                    <MenuItem value="allowlist">allowlist — nur NOTIFICATION_RECIPIENT_ALLOWLIST</MenuItem>
                    <MenuItem value="suppress">suppress — kein HTTP-Versand (nur Log „suppressed“)</MenuItem>
                  </Select>
                  <FormHelperText>
                    Umgebung GOTIFY_DELIVERY_MODE: <strong>{globalGotifyModeEnv}</strong>
                    {globalGotifyModeInDb ? ' · Aktuell gilt ein Wert aus der Datenbank' : ' · Kein DB-Override für den Modus'}
                  </FormHelperText>
                </FormControl>
                <TextField
                  label="Globale Gotify-Basis-URL"
                  fullWidth
                  size="small"
                  value={globalGotifyUrl}
                  onChange={(e) => setGlobalGotifyUrl(e.target.value)}
                  placeholder="https://push.example.com"
                  helperText="Ohne abschließenden Schrägstrich. Leer speichern entfernt die URL aus der Datenbank (es gilt dann nur noch GOTIFY_URL / pro Person)."
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    variant="contained"
                    disabled={gotifySaving}
                    onClick={async () => {
                      try {
                        setGotifySaving(true);
                        const trimmed = globalGotifyUrl.trim();
                        const d = await benachrichtigungenService.putGlobalGotifyUrl({
                          gotify_base_url: trimmed || null,
                          gotify_delivery_mode: globalGotifyDeliveryMode,
                        });
                        applyGotifySettingsResponse(d);
                        showSuccess('Gotify-URL und Versand-Modus gespeichert');
                      } catch {
                        // Interceptor
                      } finally {
                        setGotifySaving(false);
                      }
                    }}
                  >
                    {gotifySaving ? <CircularProgress size={22} /> : 'Speichern'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    disabled={gotifySaving || !globalGotifyUrl.trim()}
                    onClick={async () => {
                      try {
                        setGotifySaving(true);
                        const d = await benachrichtigungenService.putGlobalGotifyUrl({ gotify_base_url: null });
                        applyGotifySettingsResponse(d);
                        showSuccess('Globale Gotify-URL aus der Datenbank entfernt');
                      } catch {
                        // Interceptor
                      } finally {
                        setGotifySaving(false);
                      }
                    }}
                  >
                    Nur URL aus DB entfernen
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    disabled={gotifySaving || !globalGotifyModeInDb}
                    onClick={async () => {
                      try {
                        setGotifySaving(true);
                        const d = await benachrichtigungenService.putGlobalGotifyUrl({ gotify_delivery_mode: null });
                        applyGotifySettingsResponse(d);
                        showSuccess('Versand-Modus-Override entfernt — es gilt wieder GOTIFY_DELIVERY_MODE aus der Umgebung');
                      } catch {
                        // Interceptor
                      } finally {
                        setGotifySaving(false);
                      }
                    }}
                  >
                    Nur Versand-Modus: .env
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Technischer Versand über <strong>Amazon SES</strong> (kein SMTP). Werte in der Datenbank
              überschreiben die Umgebungsvariablen <strong>AWS_SES_*</strong>, <strong>EMAIL_DELIVERY_MODE</strong>{' '}
              und <strong>NOTIFICATION_RECIPIENT_ALLOWLIST</strong>. Zugangsdaten nur für Administrationsrollen;
              Speicherung erfolgt in der DB (ggf. getrennt von Code-Releases sichern).
            </Typography>
            {emailCfg && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Umgebung (.env): Region {emailCfg.env_aws_ses_region}, Absender „{emailCfg.env_aws_ses_from_email || '—'}
                &quot;, Modus <strong>{emailCfg.env_email_delivery_mode}</strong>
              </Alert>
            )}
            {emailLoading ? (
              <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 640 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="email-delivery-mode-label">E-Mail Versand-Modus</InputLabel>
                  <Select
                    labelId="email-delivery-mode-label"
                    label="E-Mail Versand-Modus"
                    value={emailDeliveryMode}
                    onChange={(e) => setEmailDeliveryMode(e.target.value as EmailDeliveryMode)}
                  >
                    <MenuItem value="send">send — produktiver Versand</MenuItem>
                    <MenuItem value="allowlist">allowlist — nur erlaubte Empfänger</MenuItem>
                    <MenuItem value="suppress">suppress — kein Versand (Entwicklung)</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="NOTIFICATION_RECIPIENT_ALLOWLIST (kommagetrennt)"
                  fullWidth
                  size="small"
                  value={emailAllowlist}
                  onChange={(e) => setEmailAllowlist(e.target.value)}
                  helperText="Nur bei Modus „allowlist“. Kommagetrennte Adressen (Kleinschreibung wird normalisiert)."
                  multiline
                  minRows={2}
                />
                <TextField
                  label="AWS-Region"
                  fullWidth
                  size="small"
                  value={emailSesRegion}
                  onChange={(e) => setEmailSesRegion(e.target.value)}
                  placeholder="eu-west-1"
                />
                <TextField
                  label="Absender-Adresse (From, in SES verifiziert)"
                  fullWidth
                  size="small"
                  value={emailSesFrom}
                  onChange={(e) => setEmailSesFrom(e.target.value)}
                />
                <TextField
                  label="AWS Access Key ID"
                  fullWidth
                  size="small"
                  value={emailAccessKey}
                  onChange={(e) => setEmailAccessKey(e.target.value)}
                  placeholder={
                    emailCfg?.ses_access_key_id_in_database
                      ? 'Unverändert lassen oder neuen Wert eintragen'
                      : 'Aus .env oder hier setzen'
                  }
                />
                <TextField
                  label="AWS Secret Access Key"
                  fullWidth
                  size="small"
                  type="password"
                  value={emailSecretKey}
                  onChange={(e) => setEmailSecretKey(e.target.value)}
                  placeholder={
                    emailCfg?.ses_secret_access_key_in_database
                      ? 'Unverändert lassen oder neuen Wert eintragen'
                      : 'Aus .env oder hier setzen'
                  }
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    disabled={emailSaving}
                    onClick={async () => {
                      try {
                        setEmailSaving(true);
                        const patch: Parameters<typeof emailAdminService.putTechnicalConfig>[0] = {
                          ses_region: emailSesRegion.trim(),
                          ses_from_email: emailSesFrom.trim(),
                          email_delivery_mode: emailDeliveryMode,
                          notification_recipient_allowlist: emailAllowlist.trim(),
                        };
                        if (emailAccessKey.trim()) patch.ses_access_key_id = emailAccessKey.trim();
                        if (emailSecretKey.trim()) patch.ses_secret_access_key = emailSecretKey.trim();
                        const d = await emailAdminService.putTechnicalConfig(patch);
                        setEmailCfg(d);
                        setEmailAccessKey('');
                        setEmailSecretKey('');
                        showSuccess('E-Mail-/SES-Konfiguration gespeichert');
                      } catch {
                        // Interceptor
                      } finally {
                        setEmailSaving(false);
                      }
                    }}
                  >
                    {emailSaving ? <CircularProgress size={22} /> : 'Speichern'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    disabled={emailSaving || (!emailCfg?.ses_access_key_id_in_database && !emailCfg?.ses_secret_access_key_in_database)}
                    onClick={async () => {
                      try {
                        setEmailSaving(true);
                        const d = await emailAdminService.putTechnicalConfig({
                          ses_access_key_id: null,
                          ses_secret_access_key: null,
                        });
                        setEmailCfg(d);
                        showSuccess('SES-Zugangsdaten aus der Datenbank entfernt');
                      } catch {
                        // Interceptor
                      } finally {
                        setEmailSaving(false);
                      }
                    }}
                  >
                    SES-Keys aus DB löschen
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    disabled={emailSaving}
                    onClick={async () => {
                      try {
                        setEmailSaving(true);
                        const d = await emailAdminService.putTechnicalConfig({
                          ses_region: null,
                          ses_from_email: null,
                          ses_access_key_id: null,
                          ses_secret_access_key: null,
                          email_delivery_mode: null,
                          notification_recipient_allowlist: null,
                        });
                        setEmailCfg(d);
                        setEmailSesRegion(d.ses_region || '');
                        setEmailSesFrom(d.ses_from_email || '');
                        setEmailDeliveryMode(d.email_delivery_mode);
                        setEmailAllowlist(d.notification_recipient_allowlist || '');
                        showSuccess('Alle E-Mail-Overrides aus der Datenbank entfernt — es gilt wieder .env');
                      } catch {
                        // Interceptor
                      } finally {
                        setEmailSaving(false);
                      }
                    }}
                  >
                    Alle E-Mail-DB-Overrides entfernen
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Card>
          <CardContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Resturlaub aus dem Vorjahr wird automatisch übertragen. Nach deutschem Recht (BUrlG/EuGH)
              verfällt Urlaub nicht automatisch — der Arbeitgeber muss Urlaub ermöglichen und ggf.
              ausdrücklich und rechtzeitig auf drohenden Verfall hinweisen. Die Hinweisfrist dient daher
              nur der internen Planung, nicht dem automatischen Kürzen des Anspruchs.
            </Typography>
            {urlaubLoading ? (
              <CircularProgress size={28} />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={urlaubAutoUebertrag}
                      onChange={(e) => setUrlaubAutoUebertrag(e.target.checked)}
                    />
                  }
                  label="Automatischen Übertrag aus dem Vorjahr aktivieren"
                />
                <TextField
                  label="Hinweisfrist Monat"
                  type="number"
                  value={urlaubHinweisMonat}
                  onChange={(e) => setUrlaubHinweisMonat(parseInt(e.target.value, 10) || 0)}
                  inputProps={{ min: 0, max: 12 }}
                  helperText="0 = keine Hinweisfrist, Standard: 3 (März) — nur Planungshinweis"
                  disabled={!urlaubAutoUebertrag}
                />
                <TextField
                  label="Hinweisfrist Tag"
                  type="number"
                  value={urlaubHinweisTag}
                  onChange={(e) => setUrlaubHinweisTag(parseInt(e.target.value, 10) || 1)}
                  inputProps={{ min: 1, max: 31 }}
                  disabled={!urlaubAutoUebertrag || urlaubHinweisMonat <= 0}
                />
                <Button
                  variant="contained"
                  disabled={urlaubSaving}
                  onClick={async () => {
                    try {
                      setUrlaubSaving(true);
                      const d = await urlaubService.updateEinstellungen({
                        auto_uebertrag_aktiv: urlaubAutoUebertrag,
                        uebertrag_hinweis_monat: urlaubHinweisMonat,
                        uebertrag_hinweis_tag: urlaubHinweisTag,
                      });
                      setUrlaubSettings(d);
                      showSuccess('Urlaubs-Einstellungen gespeichert');
                    } catch {
                      // Interceptor
                    } finally {
                      setUrlaubSaving(false);
                    }
                  }}
                >
                  {urlaubSaving ? <CircularProgress size={22} /> : 'Speichern'}
                </Button>
                {urlaubSettings && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {urlaubSettings.auto_uebertrag_aktiv
                      ? urlaubSettings.uebertrag_hinweis_monat > 0
                        ? `Planungshinweis für Vorjahres-Übertrag: ${urlaubSettings.uebertrag_hinweis_tag}.${urlaubSettings.uebertrag_hinweis_monat}. — der Anspruch bleibt danach unverändert.`
                        : 'Keine Planungs-Hinweisfrist konfiguriert.'
                      : 'Nur noch manuelle Überträge pro Mitarbeiter werden berücksichtigt.'}
                  </Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <Card>
          <CardContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Diese Angaben speisen sowohl das Rechnungs-PDF als auch die E-Rechnung
              (ZUGFeRD 2.x, Profil EN 16931). Die Norm verlangt die Pflichtangaben, daher lässt sich
              ohne sie keine E-Rechnung erzeugen. Anrede, Anschreiben und weitere Textbausteine
              liegen unter Rechnungsvorlagen.
            </Typography>
            {absenderLoading ? (
              <CircularProgress size={28} />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
                {absender && absender.fehlende_felder.length > 0 && (
                  <Alert severity="warning">
                    Unvollständig: {absender.fehlende_felder.join(', ')}. Bis diese Felder gefüllt sind,
                    schlägt die Erzeugung der E-Rechnung fehl.
                  </Alert>
                )}
                <TextField
                  label="Firmenname"
                  value={absenderForm.name ?? ''}
                  onChange={(e) => setAbsenderForm((prev) => ({ ...prev, name: e.target.value }))}
                />
                <TextField
                  label="Strasse"
                  value={absenderForm.strasse ?? ''}
                  onChange={(e) => setAbsenderForm((prev) => ({ ...prev, strasse: e.target.value }))}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="PLZ"
                    sx={{ width: 140 }}
                    value={absenderForm.plz ?? ''}
                    onChange={(e) => setAbsenderForm((prev) => ({ ...prev, plz: e.target.value }))}
                  />
                  <TextField
                    label="Ort"
                    fullWidth
                    value={absenderForm.ort ?? ''}
                    onChange={(e) => setAbsenderForm((prev) => ({ ...prev, ort: e.target.value }))}
                  />
                  <TextField
                    label="Land"
                    sx={{ width: 110 }}
                    inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                    value={absenderForm.land ?? ''}
                    onChange={(e) => setAbsenderForm((prev) => ({ ...prev, land: e.target.value }))}
                  />
                </Box>
                <TextField
                  label="USt-ID"
                  value={absenderForm.ust_id ?? ''}
                  onChange={(e) => setAbsenderForm((prev) => ({ ...prev, ust_id: e.target.value }))}
                  helperText="Umsatzsteuer-Identifikationsnummer, z. B. DE123456789"
                />
                <TextField
                  label="IBAN"
                  value={absenderForm.iban ?? ''}
                  onChange={(e) => setAbsenderForm((prev) => ({ ...prev, iban: e.target.value }))}
                  helperText="Leerzeichen werden automatisch entfernt"
                />
                <TextField
                  label="BIC"
                  value={absenderForm.bic ?? ''}
                  onChange={(e) => setAbsenderForm((prev) => ({ ...prev, bic: e.target.value }))}
                />

                <Divider textAlign="left" sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Fußzeile des PDF
                  </Typography>
                </Divider>
                <TextField
                  label="Bank"
                  value={absenderForm.bank ?? ''}
                  onChange={(e) => setAbsenderForm((prev) => ({ ...prev, bank: e.target.value }))}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Telefon"
                    fullWidth
                    value={absenderForm.telefon ?? ''}
                    onChange={(e) => setAbsenderForm((prev) => ({ ...prev, telefon: e.target.value }))}
                  />
                  <TextField
                    label="E-Mail"
                    fullWidth
                    value={absenderForm.email ?? ''}
                    onChange={(e) => setAbsenderForm((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Handelsregister"
                    fullWidth
                    value={absenderForm.handelsregister ?? ''}
                    onChange={(e) =>
                      setAbsenderForm((prev) => ({ ...prev, handelsregister: e.target.value }))
                    }
                  />
                  <TextField
                    label="Steuernummer"
                    fullWidth
                    value={absenderForm.steuernummer ?? ''}
                    onChange={(e) =>
                      setAbsenderForm((prev) => ({ ...prev, steuernummer: e.target.value }))
                    }
                  />
                </Box>
                <TextField
                  label="Geschäftsführer"
                  value={absenderForm.geschaeftsfuehrer ?? ''}
                  onChange={(e) =>
                    setAbsenderForm((prev) => ({ ...prev, geschaeftsfuehrer: e.target.value }))
                  }
                />

                <Alert
                  severity="info"
                  action={
                    <Button color="inherit" size="small" onClick={() => navigate('/rechnungen/textvorlagen')}>
                      Vorlagen
                    </Button>
                  }
                >
                  Anrede, Anschreiben, Grußformel und Zahlungshinweise werden als benannte
                  Rechnungsvorlagen gepflegt und können Kunden zugeordnet werden.
                </Alert>

                <Button
                  variant="contained"
                  disabled={absenderSaving}
                  onClick={async () => {
                    try {
                      setAbsenderSaving(true);
                      const d = await rechnungenService.updateEinstellungen(absenderForm);
                      setAbsender(d);
                      setAbsenderForm(d);
                      showSuccess('Angaben für Rechnungen und E-Rechnungen gespeichert');
                    } catch {
                      // Interceptor
                    } finally {
                      setAbsenderSaving(false);
                    }
                  }}
                >
                  {absenderSaving ? <CircularProgress size={22} /> : 'Speichern'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={6}>
        <Card>
          <CardContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Zeigt am 1. April (Europe/Berlin) einen wegklickbaren Hinweis auf dem Dashboard.
              Berichte, Rechnungen, E-Mails, Exporte und Kundenansichten bleiben unverändert.
              Standard ist an.
            </Typography>
            {aprilLoading ? (
              <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={aprilAktiv}
                    disabled={aprilSaving}
                    onChange={async (event) => {
                      const next = event.target.checked;
                      setAprilAktiv(next);
                      try {
                        setAprilSaving(true);
                        const status = await aprilscherzService.update(next);
                        setAprilAktiv(status.aktiv);
                        showSuccess(status.aktiv ? 'Aprilscherz ist an' : 'Aprilscherz ist aus');
                      } catch {
                        setAprilAktiv(!next);
                      } finally {
                        setAprilSaving(false);
                      }
                    }}
                  />
                }
                label="Aprilscherz am 1. April anzeigen"
              />
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Zeigt in der Seitenleiste und auf dem Dashboard einen wegklickbaren Satz zu Karneval,
              Ostern, Pfingsten, Halloween, Winter, Weihnachten und Neujahr (Europe/Berlin).
              Wegklicken gilt nur für das aktuelle Fenster. Daten, Berichte und Kundenansichten
              bleiben unverändert. Standard ist an.
            </Typography>
            {saisonLoading ? (
              <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={saisonAktiv}
                    disabled={saisonSaving}
                    onChange={async (event) => {
                      const next = event.target.checked;
                      setSaisonAktiv(next);
                      try {
                        setSaisonSaving(true);
                        const status = await saisonService.update(next);
                        setSaisonAktiv(status.aktiv);
                        showSuccess(status.aktiv ? 'Saisonale Gimmicks sind an' : 'Saisonale Gimmicks sind aus');
                      } catch {
                        setSaisonAktiv(!next);
                      } finally {
                        setSaisonSaving(false);
                      }
                    }}
                  />
                }
                label="Saisonale Gimmicks anzeigen"
              />
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <EditDialog
        open={statusDialogOpen}
        title={editingStatus ? 'Projektstatus bearbeiten' : 'Neuer Projektstatus'}
        name={statusName}
        nameLabel="Name"
        onNameChange={setStatusName}
        onClose={() => setStatusDialogOpen(false)}
        onSave={handleStatusSave}
        loading={loading}
      />

      <EditDialog
        open={auditStatusDialogOpen}
        title={editingAuditStatus ? 'Auditstatus bearbeiten' : 'Neuer Auditstatus'}
        name={auditStatusName}
        nameLabel="Name"
        onNameChange={setAuditStatusName}
        onClose={() => setAuditStatusDialogOpen(false)}
        onSave={handleAuditStatusSave}
        loading={loading}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Löschen bestätigen"
        message={`Möchten Sie "${itemToDelete?.displayName}" wirklich löschen?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
      />
    </Box>
  );
};

export default SystemkonfigurationPage;
